// Ventanas para crear jornadas del multievento (pestaña Líneas y menús del
// formato nuevo):
// - NuevaJornadaModal: fecha, horas (por defecto, las de la última jornada) y
//   personas (las de la última jornada con personas). La jornada nace sin
//   líneas: se añaden con "Editar menús".
// - GenerarJornadasModal: una jornada por día de un rango (solo con 0 o 1
//   jornadas, como el gestor heredado); copian las líneas de la primera.
// - DuplicarJornadaModal: la fecha de la copia de una jornada.
// Cada una llama a onConfirmar(datos) -> Promise<boolean> y se cierra si va bien.
import React, { useState } from 'react';
import ModalJornada, {
  claseBotonPrincipal, claseBotonSecundario, claseCampoJornada, claseEtiquetaJornada,
} from './ModalJornada';
import { personasPorDefectoLinea } from '../../utils/multieventoCabecera';
import { horasPorDefectoJornadaNueva, textoJornada } from '../../utils/multieventoPantallas';

const fechaLarga = (f) => (f
  ? new Date(`${f}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  : '');

const useEnvio = (onConfirmar, onCerrar) => {
  const [enviando, setEnviando] = useState(false);
  const enviar = async (datos) => {
    setEnviando(true);
    const ok = await onConfirmar(datos);
    setEnviando(false);
    if (ok) onCerrar();
  };
  return [enviando, enviar];
};

export const NuevaJornadaModal = ({ presupuesto, onConfirmar, onCerrar }) => {
  const [datos, setDatos] = useState(() => ({
    fecha: '',
    ...horasPorDefectoJornadaNueva(presupuesto),
    num_personas: String(personasPorDefectoLinea(presupuesto, null, 10)),
  }));
  const [enviando, enviar] = useEnvio(onConfirmar, onCerrar);
  const personas = parseInt(datos.num_personas, 10) || 0;
  const valido = !!datos.fecha && !!datos.hora_inicio && !!datos.hora_fin && personas > 0;

  return (
    <ModalJornada
      titulo="Añadir jornada"
      subtitulo="La jornada nace sin líneas: después añade las que tocan ese día con Editar menús."
      onCerrar={onCerrar}
      testid="modal-nueva-jornada"
      pie={(
        <>
          <button type="button" onClick={onCerrar} className={claseBotonSecundario}>Cancelar</button>
          <button
            type="button"
            disabled={!valido || enviando}
            onClick={() => enviar({ ...datos, num_personas: personas })}
            className={claseBotonPrincipal}
            data-testid="nueva-jornada-crear"
          >
            {enviando ? 'Creando...' : 'Crear jornada'}
          </button>
        </>
      )}
    >
      <label className={claseEtiquetaJornada}>
        Fecha
        <input type="date" value={datos.fecha} onChange={(e) => setDatos(d => ({ ...d, fecha: e.target.value }))}
          className={claseCampoJornada} data-testid="nueva-jornada-fecha" />
        {datos.fecha && <span className="font-normal text-[#33443C]">{fechaLarga(datos.fecha)}</span>}
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className={claseEtiquetaJornada}>
          Hora de inicio
          <input type="time" value={datos.hora_inicio} onChange={(e) => setDatos(d => ({ ...d, hora_inicio: e.target.value }))}
            className={claseCampoJornada} />
        </label>
        <label className={claseEtiquetaJornada}>
          Hora de fin
          <input type="time" value={datos.hora_fin} onChange={(e) => setDatos(d => ({ ...d, hora_fin: e.target.value }))}
            className={claseCampoJornada} />
        </label>
      </div>
      <label className={claseEtiquetaJornada}>
        Personas
        <input type="number" min="1" value={datos.num_personas}
          onChange={(e) => setDatos(d => ({ ...d, num_personas: e.target.value }))}
          className={claseCampoJornada} data-testid="nueva-jornada-personas" />
      </label>
    </ModalJornada>
  );
};

export const GenerarJornadasModal = ({ presupuesto, onConfirmar, onCerrar }) => {
  const [datos, setDatos] = useState(() => ({
    fecha_inicio: '',
    fecha_fin: '',
    excluir_fines_semana: true,
    ...horasPorDefectoJornadaNueva(presupuesto),
    num_personas: String(personasPorDefectoLinea(presupuesto, null, 10)),
  }));
  const [enviando, enviar] = useEnvio(onConfirmar, onCerrar);
  const personas = parseInt(datos.num_personas, 10) || 0;
  const valido = !!datos.fecha_inicio && !!datos.fecha_fin && datos.fecha_inicio <= datos.fecha_fin && personas > 0;

  return (
    <ModalJornada
      titulo="Generar jornadas"
      subtitulo="Se crea una jornada por cada día del rango (sin repetir las fechas que ya tienen jornada), con las líneas de la primera jornada."
      onCerrar={onCerrar}
      testid="modal-generar-jornadas"
      pie={(
        <>
          <button type="button" onClick={onCerrar} className={claseBotonSecundario}>Cancelar</button>
          <button
            type="button"
            disabled={!valido || enviando}
            onClick={() => enviar({ ...datos, num_personas: personas })}
            className={claseBotonPrincipal}
            data-testid="generar-jornadas-confirmar"
          >
            {enviando ? 'Generando...' : 'Generar jornadas'}
          </button>
        </>
      )}
    >
      <div className="grid grid-cols-2 gap-3">
        <label className={claseEtiquetaJornada}>
          Desde
          <input type="date" value={datos.fecha_inicio} onChange={(e) => setDatos(d => ({ ...d, fecha_inicio: e.target.value }))}
            className={claseCampoJornada} data-testid="generar-jornadas-inicio" />
        </label>
        <label className={claseEtiquetaJornada}>
          Hasta
          <input type="date" value={datos.fecha_fin} onChange={(e) => setDatos(d => ({ ...d, fecha_fin: e.target.value }))}
            className={claseCampoJornada} data-testid="generar-jornadas-fin" />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className={claseEtiquetaJornada}>
          Hora de inicio
          <input type="time" value={datos.hora_inicio} onChange={(e) => setDatos(d => ({ ...d, hora_inicio: e.target.value }))}
            className={claseCampoJornada} />
        </label>
        <label className={claseEtiquetaJornada}>
          Hora de fin
          <input type="time" value={datos.hora_fin} onChange={(e) => setDatos(d => ({ ...d, hora_fin: e.target.value }))}
            className={claseCampoJornada} />
        </label>
      </div>
      <label className={claseEtiquetaJornada}>
        Personas por jornada
        <input type="number" min="1" value={datos.num_personas}
          onChange={(e) => setDatos(d => ({ ...d, num_personas: e.target.value }))}
          className={claseCampoJornada} />
      </label>
      <label className="flex items-center gap-2 text-sm text-[#33443C]">
        <input type="checkbox" checked={datos.excluir_fines_semana}
          onChange={(e) => setDatos(d => ({ ...d, excluir_fines_semana: e.target.checked }))} />
        Sin sábados ni domingos
      </label>
    </ModalJornada>
  );
};

export const DuplicarJornadaModal = ({ jornada, numero, onConfirmar, onCerrar }) => {
  const [fecha, setFecha] = useState('');
  const [enviando, enviar] = useEnvio(onConfirmar, onCerrar);
  return (
    <ModalJornada
      titulo="Duplicar jornada"
      subtitulo={`${textoJornada(jornada, numero)}: la copia lleva sus líneas, horas y personas.`}
      onCerrar={onCerrar}
      testid={`modal-duplicar-jornada-${jornada?.id}`}
      pie={(
        <>
          <button type="button" onClick={onCerrar} className={claseBotonSecundario}>Cancelar</button>
          <button type="button" disabled={!fecha || enviando} onClick={() => enviar(fecha)}
            className={claseBotonPrincipal} data-testid="duplicar-jornada-confirmar">
            {enviando ? 'Duplicando...' : 'Duplicar'}
          </button>
        </>
      )}
    >
      <label className={claseEtiquetaJornada}>
        Fecha de la jornada nueva
        <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
          className={claseCampoJornada} data-testid="duplicar-jornada-fecha" />
        {fecha && <span className="font-normal text-[#33443C]">{fechaLarga(fecha)}</span>}
      </label>
    </ModalJornada>
  );
};

export default NuevaJornadaModal;
